import { Controller, useFormContext } from 'react-hook-form';
import { FormValues } from '../schema';
import { VolatilityBotFormValues } from '../../volatility/schema';
import NumberInput from '../../../components/inputs/number-input';
import Checkbox from '../../../components/inputs/Checkbox';
import Tooltip from 'modules/shared-components/tooltip';

export function StopLoss({ isVolatilityBot }: { isVolatilityBot?: boolean }) {
  type CombinedFormValues = VolatilityBotFormValues | FormValues;
  const { watch, setValue, control } = useFormContext<CombinedFormValues>();
  const trailingEnabled = watch('tradingSettings.stopLoss.isTrailingEnabled');

  const sl = watch('tradingSettings.stopLoss');

  return (
    <div className="xxs:space-y-1">
      <div className="flex">
        <Tooltip text="Auto-sell your tokens when they hit the stop loss price threshold. Add stop-loss trailing to move your stop loss to breakeven automatically.">
          <div>
            <div className="flex items-center xxs:gap-1">
              <Checkbox
                className="lg:w-4 lg:h-4 xxs:w-5 xxs:h-5"
                checked={!!sl}
                onChange={(e) => {
                  const checked = e.currentTarget.checked;
                  if (
                    !checked &&
                    (watch('botSettings.entries.autoRetryEnabled') ||
                      watch('tradingSettings.safetyMeasures.autoRetryEnabled'))
                  ) {
                    setValue(
                      'tradingSettings.safetyMeasures.autoRetry.stopLoss.active',
                      false
                    );
                    setValue(
                      'botSettings.entries.autoRetry.stopLoss.active',
                      false
                    );
                  }

                  if (checked) {
                    setValue(
                      'tradingSettings.safetyMeasures.autoRetryEnabled',
                      true
                    );
                    setValue(
                      'tradingSettings.safetyMeasures.autoRetry.stopLoss.active',
                      true
                    );
                    setValue(
                      'tradingSettings.safetyMeasures.autoRetry.stopLoss.nRetries',
                      1
                    );
                    setValue('botSettings.entries.autoRetryEnabled', true);
                    setValue(
                      'botSettings.entries.autoRetry.stopLoss.active',
                      true
                    );
                    setValue(
                      'botSettings.entries.autoRetry.stopLoss.nRetries',
                      1
                    );
                  }
                  setValue(
                    'tradingSettings.stopLoss',
                    checked
                      ? {
                          deviation: 0,
                          dynamic: true,
                          isTrailingEnabled: false,
                          threshold: 50,
                        }
                      : null,
                    {
                      shouldDirty: true,
                    }
                  );
                }}
              />
              <div className="xxs:text-sm xxs:text-dex-white-secondary hover:text-dex-white">
                Stop Loss
              </div>
            </div>
          </div>
        </Tooltip>
      </div>

      {sl ? (
        <>
          <Controller
            name={`tradingSettings.stopLoss.threshold`}
            defaultValue={'' as any}
            control={control}
            render={({ field, fieldState: { error } }) => {
              return (
                <NumberInput
                  {...field}
                  prefix={'-'}
                  suffix={<>%</>}
                  inputSize="small"
                  extraLabel={
                    <div className="flex flex-nowrap">
                      <Checkbox
                        disabled={trailingEnabled}
                        id="stopLoss.dynamic"
                        checked={watch('tradingSettings.stopLoss.dynamic')}
                        onChange={(ev) => {
                          setValue(
                            'tradingSettings.stopLoss.dynamic',
                            ev.target.checked
                          );
                        }}
                      />
                      <Checkbox.Label htmlFor="stopLoss.dynamic">
                        <Tooltip
                          className="xxs:text-dex-white-secondary hover:text-dex-white"
                          text={
                            trailingEnabled
                              ? 'Cannot be disabled if trailing stop loss is active. '
                              : 'Enable this to set a stop loss that adjusts relative to the average entry price. If disabled, the stop loss will remain fixed based on the initial entry price.'
                          }
                        >
                          <div>Dynamic</div>
                        </Tooltip>
                      </Checkbox.Label>
                    </div>
                  }
                  label="Threshold"
                  error={error?.message}
                />
              );
            }}
          />

          {isVolatilityBot && (
            <div className="flex flex-nowrap items-center">
              <Checkbox
                id="botSettings.entries.removeTokenWhenSlHits"
                disabled={!sl}
                className="xxs:w-5 xxs:h-5 lg:w-4 lg:h-4"
                checked={watch('botSettings.entries.removeTokenWhenSlHits')}
                onChange={(ev) => {
                  setValue(
                    'botSettings.entries.removeTokenWhenSlHits',
                    ev.target.checked
                  );
                }}
              />
              <Checkbox.Label htmlFor="botSettings.entries.removeTokenWhenSlHits">
                <Tooltip
                  className="xxs:text-dex-white-secondary hover:text-dex-white xxs:text-sm"
                  text={
                    sl
                      ? `Enable this to automatically remove a token from the whitelisted tokens when the stop loss is hit.`
                      : 'Enable stop-loss to activate this.'
                  }
                >
                  <div>Auto-Remove Tokens after SL</div>
                </Tooltip>
              </Checkbox.Label>
            </div>
          )}

          <div className="flex justify-between items-center xxs:space-x-1">
            <div className="flex flex-nowrap items-center">
              <Checkbox
                id="trailing"
                className="lg:w-4 lg:h-4 xxs:h-5 xxs:w-5"
                checked={watch('tradingSettings.stopLoss.isTrailingEnabled')}
                onChange={(ev) => {
                  setValue(
                    'tradingSettings.stopLoss.isTrailingEnabled',
                    ev.target.checked
                  );
                  setValue(
                    'tradingSettings.stopLoss.deviation',
                    ev.target.checked ? 50 : null
                  );
                  setValue('tradingSettings.stopLoss.dynamic', true);
                }}
              />
              <Checkbox.Label htmlFor="trailing">
                <Tooltip text="The bot not only continually tracks the profit and loss (P&L) of your trade but also dynamically adjusts the stop loss level based on the all time high P&L. Read the docs for more info.">
                  <div className="xxs:text-sm xxs:text-dex-white-secondary hover:text-dex-white">
                    Trailing Deviation
                  </div>
                </Tooltip>
              </Checkbox.Label>
            </div>
            {watch('tradingSettings.stopLoss.isTrailingEnabled') && (
              <div className="flex">
                <Controller
                  name={`tradingSettings.stopLoss.deviation`}
                  defaultValue={'' as any}
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <NumberInput
                        {...(field as any)}
                        prefix={'+'}
                        inputSize="small"
                        suffix={<>%</>}
                        error={error?.message}
                      />
                    );
                  }}
                />
              </div>
            )}
          </div>
        </>
      ) : (
        ''
      )}
    </div>
  );
}
